import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * On native Android (Capacitor), schedules real OS notifications for every
 * upcoming one-time calendar reminder. When the user taps the notification,
 * we bring the app back to /app so they land inside the workspace.
 *
 * Web is a no-op — the existing in-app ReminderBell already handles that
 * surface. Recurring ("constant") reminders stay calendar-only, matching
 * the ReminderBell rule.
 *
 * Idempotency: every sync cancels our previously-scheduled notifications
 * (identified by extra.origin === "notelosh-reminder") and re-schedules
 * the current set, so edits/deletions on the web propagate cleanly.
 */

const isNative = () =>
  typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

async function loadPlugin() {
  if (!isNative()) return null;
  try {
    const mod = await import("@capacitor/local-notifications");
    return mod.LocalNotifications;
  } catch {
    return null;
  }
}

// Stable positive int32 id derived from the reminder UUID (djb2).
function hashId(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h) % 2147483000; // stay well below int32 max
}

export default function ReminderNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const sync = useCallback(async () => {
    if (!isNative() || !user) return;
    const LN = await loadPlugin();
    if (!LN) return;

    // Ensure permission (Android 13+ shows the prompt once). If denied we
    // silently bail — the in-app bell still works.
    try {
      const perm = await LN.checkPermissions();
      if (perm.display !== "granted") {
        const req = await LN.requestPermissions();
        if (req.display !== "granted") return;
      }
    } catch { return; }

    // Cancel any pending notifications we scheduled earlier.
    try {
      const pending = await LN.getPending();
      const ours = (pending?.notifications || [])
        .filter((n) => n?.extra?.origin === "notelosh-reminder")
        .map((n) => ({ id: n.id }));
      if (ours.length) await LN.cancel({ notifications: ours });
    } catch { /* ignore */ }

    // Fetch fresh reminders.
    let reminders = [];
    try {
      const r = await api.get("/reminders");
      reminders = r.data || [];
    } catch { return; }

    const now = Date.now();
    const toSchedule = [];
    const seenIds = new Set();
    for (const r of reminders) {
      if ((r.recurrence || "none") !== "none") continue; // recurring = calendar only
      if (!r.date) continue;
      const at = new Date(`${r.date}T${r.time || "09:00"}:00`);
      if (isNaN(at.getTime())) continue;
      if (at.getTime() <= now) continue; // already due — bell handles it
      let id = hashId(r.reminder_id || `${r.date}-${r.time}-${r.text || ""}`);
      // guarantee uniqueness in this batch
      while (seenIds.has(id)) id = (id + 1) % 2147483000;
      seenIds.add(id);
      toSchedule.push({
        id,
        title: "NoteLosh reminder",
        body: r.text || "You have a reminder",
        schedule: { at },
        extra: {
          origin: "notelosh-reminder",
          reminder_id: r.reminder_id,
          title_id: r.title_id || null,
          item_id: r.item_id || null,
        },
      });
    }
    if (toSchedule.length) {
      try { await LN.schedule({ notifications: toSchedule }); } catch { /* ignore */ }
    }
  }, [user]);

  useEffect(() => { sync(); }, [sync]);

  useEffect(() => {
    if (!isNative()) return;
    const onChange = () => sync();
    window.addEventListener("notelosh:reminder-created", onChange);
    window.addEventListener("focus", onChange);
    return () => {
      window.removeEventListener("notelosh:reminder-created", onChange);
      window.removeEventListener("focus", onChange);
    };
  }, [sync]);

  // Tap-to-open: send the user into the app.
  useEffect(() => {
    if (!isNative()) return;
    let sub;
    (async () => {
      const LN = await loadPlugin();
      if (!LN) return;
      try {
        sub = await LN.addListener("localNotificationActionPerformed", (payload) => {
          const extra = payload?.notification?.extra || {};
          if (extra.origin !== "notelosh-reminder") return;
          // Land inside the note the reminder came from, not just the workspace.
          const params = new URLSearchParams();
          if (extra.title_id) params.set("title", extra.title_id);
          if (extra.item_id) params.set("item", extra.item_id);
          const qs = params.toString();
          navigate(qs ? `/app?${qs}` : "/app");
        });
      } catch { /* ignore */ }
    })();
    return () => { try { sub?.remove?.(); } catch { /* ignore */ } };
  }, [navigate]);

  return null;
}
